import { useState, type FC, useEffect, type ReactNode, useRef, ComponentProps } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Form } from 'antd';
import type { Arg0, WithOptionalDataTest } from 'types';
import { Button, InputText, Select, Space, Text } from '@g2u/ui-kit';

import { useText } from 'helpers/hooks/useText';
import { addSpacesToNumber, useSearchParamsInUrl } from 'helpers';
import styles from './search-customer.module.scss';
import { useCustomerSearch } from 'helpers/hooks/useCustomerSearch';
import { CustomerList } from '../customer-list';
import { useLocation } from 'react-router-dom';

type Props = {
  hasScript?: boolean;
  actions?: ComponentProps<typeof CustomerList>['actions'];
  hasQuickSearch?: boolean;
  emptyContent?: (data: FormFields) => ReactNode;
  syncParamsToUrl?: boolean;
} & WithOptionalDataTest;

type SearchParams = Required<Arg0<typeof useCustomerSearch>>;
type SearchByValues = SearchParams['searchBy'];
type SearchByOption = {
  label: ReactNode;
  value: SearchByValues;
};

const searchByOptions: SearchByOption[] = [
  {
    label: <>Name</>,
    value: 'name',
  },
  {
    label: <>ID</>,
    value: 'id',
  },
  {
    label: <>Email</>,
    value: 'email',
  },
  {
    label: <>Address</>,
    value: 'address',
  },
  {
    label: <>Mobile</>,
    value: 'mobile',
  },
  {
    label: <>Phone</>,
    value: 'phone',
  },
  {
    label: <>Parent Company Name</>,
    value: 'parent_company_name',
  },
];

type FormFields = {
  customerType: SearchParams['filter'];
  searchBy: SearchParams['searchBy'];
  searchedValue: SearchParams['searchValue'];
};

export const SearchCustomer: FC<Props> = ({
  hasScript,
  actions,
  emptyContent,
  hasQuickSearch = false,
  syncParamsToUrl = true,
}) => {
  const { getText } = useText();

  const [form] = Form.useForm<FormFields>();

  const { state } = useLocation();
  const isInitQuickSearch = !!state?.searchValue || state?.searchValue === '';

  const initialSearchParams: SearchParams = {
    searchBy: isInitQuickSearch ? 'all' : 'mobile',
    searchValue: isInitQuickSearch ? state?.searchValue : '',
    filter: '',
    page: 1,
    recordsPerPage: 5,
  };

  const [searchParamsFromLocal, setSearchParamsToLocal] = useState<SearchParams>(initialSearchParams);
  const [searchParamsFromUrl, setSearchParamsToUrl] = useSearchParamsInUrl<SearchParams>(
    syncParamsToUrl && initialSearchParams,
  );

  const { filter, searchBy, searchValue, page, recordsPerPage } = syncParamsToUrl
    ? searchParamsFromUrl
    : searchParamsFromLocal;
  const setSearchParams = (params: Partial<SearchParams>) => {
    syncParamsToUrl
      ? setSearchParamsToUrl(params)
      : setSearchParamsToLocal((oldParams) => ({ ...oldParams, ...params }));
  };

  const formSearchBy = Form.useWatch('searchBy', { form });
  const searchByNumber = formSearchBy && /phone|mobile|id/.test(formSearchBy);
  const prevSearchByNumber = useRef<boolean>(undefined);

  useEffect(() => {
    if (searchByNumber === true && prevSearchByNumber.current === false) form.setFieldValue('searchedValue', '');
    prevSearchByNumber.current = searchByNumber;
  }, [searchByNumber]);

  useEffect(() => {
    const isQuickSearch = !!state?.searchValue || state?.searchValue === '';
    isQuickSearch &&
      setSearchParams({
        searchBy: 'all',
        searchValue: state?.searchValue,
      });

    if (state?.searchValue) form.setFieldValue('searchedValue', state.searchValue);
  }, [state]);

  const handleFormSubmit = (fields: FormFields) => {
    // set search form changes
    setSearchParams({
      filter: fields.customerType,
      searchBy: fields.searchBy,
      searchValue: fields.searchedValue,
      page: 1, // reset pagination state
    });
  };

  const [customers, total = 0] = useCustomerSearch({ filter, searchBy, searchValue, page, recordsPerPage });

  const paginationMetadata: ComponentProps<typeof CustomerList>['paginationMetadata'] = {
    total,
    current_page: page,
    onChange(page, pageSize) {
      setSearchParams({
        page,
        recordsPerPage: pageSize,
      });
    },
    pageSize: recordsPerPage,
  };

  return (
    <>
      <div className={styles.searchCustomerFile}>
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          {hasScript && (
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall} className="script-box">
              <Text.$>{getText('booking.start.returnGreeting')}</Text.$>
              <Text.$>Thank you.</Text.$>
              <Text.$ mark>{getText('booking.start.confirmFullAddress')}</Text.$>
              <Text.$ type="secondary" italic>
                {getText('booking.start.unableToConfirmAddress')}
              </Text.$>
            </Space.$>
          )}
          <Form form={form} layout="vertical" className={styles.searchCustomerFile__form} onFinish={handleFormSubmit}>
            <Select.$
              style={{ width: '100%' }}
              name="customerType"
              rules={[{}]}
              label="Customer type:"
              dataTestId="customer-type-select"
              initialValue={filter}
              suffixIcon={undefined}
              className={styles.searchCustomerFile__formSelect}
              options={[
                {
                  label: <>All</>,
                  value: '',
                },
                {
                  label: <>Personal</>,
                  value: 'P',
                },
                {
                  label: <>Company</>,
                  value: 'C',
                },
              ]}
            />
            <Space.$ compact fullWidth>
              <Select.$
                name="searchBy"
                rules={[{}]}
                dataTestId="search-by-select"
                className={styles.searchCustomerFile__formSelect}
                initialValue={searchBy}
                label=" "
                suffixIcon={undefined}
                options={(hasQuickSearch
                  ? ([
                      {
                        label: <>All</>,
                        value: 'all',
                      },
                    ] as SearchByOption[])
                  : []
                ).concat(searchByOptions)}
              />
              {searchByNumber ? (
                <InputText.$
                  label=" "
                  fullWidth
                  name="searchedValue"
                  initialValue={searchValue}
                  placeholder=""
                  dataTestId="searched-value"
                  noMargin
                  className={styles.searchCustomerFile__formSearch}
                  maxLength={12}
                  formatter={(val) => addSpacesToNumber(val?.toString(), [4, 7])}
                />
              ) : (
                <InputText.$
                  label=" "
                  fullWidth
                  name="searchedValue"
                  initialValue={searchValue}
                  placeholder=""
                  dataTestId="searched-value"
                  noMargin
                  className={styles.searchCustomerFile__formSearch}
                />
              )}
              <Form.Item label=" ">
                <Button.$
                  type={Button.Type.Primary}
                  dataTestId="search-submit-btn"
                  onClick={() => null}
                  icon={<SearchOutlined />}
                  htmlType="submit"
                />
              </Form.Item>
            </Space.$>
          </Form>
        </Space.$>
        <CustomerList customers={customers} actions={actions} paginationMetadata={paginationMetadata} />
      </div>
      {customers && !customers?.length ? emptyContent?.(form.getFieldsValue()) : null}
    </>
  );
};
